import React, { useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReportCountBadge from './ReportCountBadge';
import FloatingFieldShell from './FloatingFieldShell';

const ReportMultiSelectFilter = ({
  label,
  options = [],
  value = [],
  onChange,
  required = false,
  loading = false,
  disabled = false,
  error = false,
  searchPlaceholder = 'Search...',
  disabledLabel = '',
  disabledTooltip = '',
  emptyText = 'No results.',
  width = 150,
  id,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const open = Boolean(anchorEl);
  const selectedCount = value.filter((item) => item.toLowerCase() !== 'all').length;

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return options;
    }

    return options.filter(
      (option) => option.label.toLowerCase().includes(query) || String(option.value).toLowerCase().includes(query),
    );
  }, [options, search]);

  const handleOpen = (event) => {
    if (disabled || loading) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const toggleOption = (option) => {
    if (option.disabled) {
      return;
    }

    if (value.includes(option.value)) {
      onChange(value.filter((item) => item !== option.value));
      return;
    }

    onChange([...value, option.value]);
  };

  return (
    <FloatingFieldShell
      label={label}
      required={required}
      error={error}
      disabled={disabled}
      active={open}
      floated={open || selectedCount > 0}
      sx={{ width, minWidth: width, flexShrink: 0 }}
    >
      <Box
        id={id}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        tabIndex={disabled ? -1 : 0}
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen(event);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          height: '100%',
          px: 1.5,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {selectedCount > 0 && (
            <>
              <Typography variant="body2" noWrap sx={{ fontSize: 12 }}>
                {label}
              </Typography>
              <ReportCountBadge count={selectedCount} />
            </>
          )}
        </Box>
        {loading ? (
          <CircularProgress size={14} />
        ) : (
          <KeyboardArrowDownIcon fontSize="small" sx={{ opacity: 0.5, flexShrink: 0 }} />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: anchorEl?.offsetWidth ? Math.max(anchorEl.offsetWidth, 280) : 280,
              borderRadius: 1.5,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List dense disablePadding sx={{ maxHeight: 260, overflowY: 'auto', py: 0.5 }}>
          {filteredOptions.length === 0 && (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {emptyText}
              </Typography>
            </Box>
          )}

          {filteredOptions.map((option) => {
            const isSelected = value.includes(option.value);
            const isDisabled = Boolean(option.disabled);

            const row = (
              <ListItemButton
                disabled={isDisabled}
                onClick={() => toggleOption(option)}
                selected={isSelected && !isDisabled}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  gap: 1,
                  '&:hover': {
                    bgcolor: isDisabled ? 'transparent' : (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Checkbox
                  size="small"
                  checked={isSelected}
                  disabled={isDisabled}
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0.25, color: 'grey.400', '&.Mui-checked': { color: 'primary.main' } }}
                />
                <Typography
                  variant="body2"
                  noWrap
                  title={option.label}
                  sx={{ flex: 1, color: isDisabled ? 'text.disabled' : 'text.primary' }}
                >
                  {option.label}
                </Typography>
                {isDisabled && disabledLabel && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', fontWeight: 600, letterSpacing: 0.4, flexShrink: 0, fontSize: 10 }}
                  >
                    {disabledLabel}
                  </Typography>
                )}
              </ListItemButton>
            );

            if (isDisabled && disabledTooltip) {
              return (
                <Tooltip key={option.value} title={disabledTooltip} placement="right" arrow>
                  <Box>{row}</Box>
                </Tooltip>
              );
            }

            return <React.Fragment key={option.value}>{row}</React.Fragment>;
          })}
        </List>
      </Popover>
    </FloatingFieldShell>
  );
};

export default ReportMultiSelectFilter;
